import { openSignatureModal, removeSignatureModal } from "./signature_modal"

/**
 * SignaturePad LiveView hook
 *
 * A standalone signature capture pad for use in native form fields.
 * Shows "Click to sign" prompt, opens a draw/type modal on click,
 * stores the result as a base64 data URL in a hidden input.
 *
 * Data attributes:
 *   data-field-name  - The form field name for the hidden input
 *   data-label       - Display label (e.g. "Signature", "Initials")
 *   data-type        - "signature" or "initials"
 *   data-signer-name   - prefills the Type tab (signature only)
 *   data-consent-text  - e-sign consent line shown in the modal
 */
const PdfSignaturePad = {
  mounted() {
    this.fieldName = this.el.dataset.fieldName
    this.label = this.el.dataset.label || "Signature"
    this.isInitials = this.el.dataset.type === "initials"
    this.signerName = this.el.dataset.signerName || ""
    this.consentText = this.el.dataset.consentText || ""
    this.dataUrl = ""

    this._hiddenInput = this.el.querySelector("input[type=hidden]")
    this._preview = this.el.querySelector("[data-sig-preview]")
    this._prompt = this.el.querySelector("[data-sig-prompt]")

    this.el.querySelector("[data-sig-trigger]").addEventListener("click", () => {
      this._openModal()
    })

    this.el.querySelector("[data-sig-clear-btn]")?.addEventListener("click", (e) => {
      e.stopPropagation()
      this._clear()
    })
  },

  destroyed() {
    this._removeModal()
  },

  _clear() {
    this.dataUrl = ""
    this._hiddenInput.value = ""
    this._preview.style.display = "none"
    this._preview.innerHTML = ""
    this._prompt.style.display = ""
    const clearBtn = this.el.querySelector("[data-sig-clear-btn]")
    if (clearBtn) clearBtn.style.display = "none"

    // Notify PdfViewer to clear the preview
    window.dispatchEvent(
      new CustomEvent("pdf-field-value-changed", {
        detail: { name: this.fieldName, value: "", type: "signature" },
      }),
    )
  },

  _setSignature(dataUrl) {
    this.dataUrl = dataUrl
    this._hiddenInput.value = dataUrl

    // Notify PdfViewer for live preview on the PDF
    window.dispatchEvent(
      new CustomEvent("pdf-field-value-changed", {
        detail: { name: this.fieldName, value: dataUrl, type: "signature" },
      }),
    )

    if (dataUrl) {
      const img = document.createElement("img")
      img.src = dataUrl
      img.style.cssText = "max-width:100%;max-height:100%;object-fit:contain;"
      this._preview.innerHTML = ""
      this._preview.appendChild(img)
      this._preview.style.display = "flex"
      this._prompt.style.display = "none"
      const clearBtn = this.el.querySelector("[data-sig-clear-btn]")
      if (clearBtn) clearBtn.style.display = ""
    } else {
      this._clear()
    }
  },

  _openModal() {
    openSignatureModal({
      isInitials: this.isInitials,
      initialDataUrl: this.dataUrl,
      signerName: this.signerName,
      consentText: this.consentText,
      onAccept: (dataUrl) => this._setSignature(dataUrl),
    })
  },

  _removeModal() {
    removeSignatureModal()
  },
}

export default PdfSignaturePad
